import React, { useState } from 'react'
import CsvOptionImport from './CsvOptionImport'

export type QuestionType = 'BOOLEAN' | 'TEXT' | 'RATING' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'COUNTRY'
export type ScoringType = 'binary' | 'direct_scale' | 'weighted_options' | 'qualitative' | ''

export interface ScoringConfig {
  type: ScoringType
  true_value?: number
  false_value?: number
  min_value?: number
  max_value?: number
  option_weights?: Record<string, number>
}

export interface QuestionData {
  text: string
  type: QuestionType
  required: boolean
  order: number
  options: string[]
  scoring_config: ScoringConfig | null
}

interface QuestionBuilderProps {
  questions: QuestionData[]
  onChange: (questions: QuestionData[]) => void
}

const TYPE_LABELS: Record<QuestionType, string> = {
  BOOLEAN: 'Yes / No',
  TEXT: 'Text Response',
  RATING: 'Rating (0-5)',
  SINGLE_CHOICE: 'Single Choice',
  MULTIPLE_CHOICE: 'Multiple Choice',
  COUNTRY: 'Country Selector',
}

const DEFAULT_SCORING: Record<QuestionType, ScoringConfig | null> = {
  BOOLEAN: { type: 'binary', true_value: 10, false_value: 0 },
  RATING: { type: 'direct_scale', min_value: 0, max_value: 5 },
  SINGLE_CHOICE: { type: 'weighted_options', option_weights: {} },
  MULTIPLE_CHOICE: { type: 'weighted_options', option_weights: {} },
  TEXT: { type: 'qualitative' },
  COUNTRY: null,
}

const QuestionBuilder: React.FC<QuestionBuilderProps> = ({ questions, onChange }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const addQuestion = () => {
    const newQ: QuestionData = {
      text: '',
      type: 'BOOLEAN',
      required: true,
      order: questions.length,
      options: [],
      scoring_config: null,
    }
    onChange([...questions, newQ])
    setExpandedIndex(questions.length)
  }

  const updateQuestion = (index: number, updates: Partial<QuestionData>) => {
    const updated = questions.map((q, i) => (i === index ? { ...q, ...updates } : q))
    onChange(updated)
  }

  const removeQuestion = (index: number) => {
    const updated = questions.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i }))
    onChange(updated)
    if (expandedIndex === index) setExpandedIndex(null)
    else if (expandedIndex !== null && expandedIndex > index) setExpandedIndex(expandedIndex - 1)
  }

  const handleTypeChange = (index: number, newType: QuestionType) => {
    const q = questions[index]
    const updates: Partial<QuestionData> = { type: newType }

    // Reset options for types that don't use them
    const hasOptions = newType === 'SINGLE_CHOICE' || newType === 'MULTIPLE_CHOICE'
    if (!hasOptions) {
      updates.options = []
    } else if (q.options.length === 0) {
      updates.options = ['Option 1', 'Option 2']
    }

    // Update scoring config if enabled
    if (q.scoring_config) {
      updates.scoring_config = DEFAULT_SCORING[newType]
      if (hasOptions && updates.scoring_config?.type === 'weighted_options') {
        const weights: Record<string, number> = {}
        const opts = updates.options || q.options
        opts.forEach((opt, i) => { weights[opt] = opts.length - i })
        updates.scoring_config = { ...updates.scoring_config, option_weights: weights }
      }
    }

    updateQuestion(index, updates)
  }

  const toggleScoring = (index: number) => {
    const q = questions[index]
    if (q.scoring_config) {
      updateQuestion(index, { scoring_config: null })
    } else {
      const defaultConfig = DEFAULT_SCORING[q.type]
      if ((q.type === 'SINGLE_CHOICE' || q.type === 'MULTIPLE_CHOICE') && defaultConfig?.type === 'weighted_options') {
        const weights: Record<string, number> = {}
        q.options.forEach((opt, i) => { weights[opt] = q.options.length - i })
        updateQuestion(index, { scoring_config: { ...defaultConfig, option_weights: weights } })
      } else {
        updateQuestion(index, { scoring_config: defaultConfig ? { ...defaultConfig } : null })
      }
    }
  }

  const addOption = (index: number) => {
    const q = questions[index]
    const newOpt = `Option ${q.options.length + 1}`
    const newOptions = [...q.options, newOpt]
    const updates: Partial<QuestionData> = { options: newOptions }
    if (q.scoring_config?.type === 'weighted_options') {
      updates.scoring_config = {
        ...q.scoring_config,
        option_weights: { ...(q.scoring_config.option_weights || {}), [newOpt]: 0 }
      }
    }
    updateQuestion(index, updates)
  }

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    const q = questions[qIndex]
    const oldOpt = q.options[optIndex]
    const newOptions = q.options.map((o, i) => (i === optIndex ? value : o))
    const updates: Partial<QuestionData> = { options: newOptions }
    if (q.scoring_config?.type === 'weighted_options' && q.scoring_config.option_weights) {
      const newWeights = { ...q.scoring_config.option_weights }
      const weight = newWeights[oldOpt] ?? 0
      delete newWeights[oldOpt]
      newWeights[value] = weight
      updates.scoring_config = { ...q.scoring_config, option_weights: newWeights }
    }
    updateQuestion(qIndex, updates)
  }

  const removeOption = (qIndex: number, optIndex: number) => {
    const q = questions[qIndex]
    const removedOpt = q.options[optIndex]
    const newOptions = q.options.filter((_, i) => i !== optIndex)
    const updates: Partial<QuestionData> = { options: newOptions }
    if (q.scoring_config?.type === 'weighted_options' && q.scoring_config.option_weights) {
      const newWeights = { ...q.scoring_config.option_weights }
      delete newWeights[removedOpt]
      updates.scoring_config = { ...q.scoring_config, option_weights: newWeights }
    }
    updateQuestion(qIndex, updates)
  }

  const updateOptionWeight = (qIndex: number, option: string, weight: number) => {
    const q = questions[qIndex]
    if (q.scoring_config?.type === 'weighted_options') {
      updateQuestion(qIndex, {
        scoring_config: {
          ...q.scoring_config,
          option_weights: { ...(q.scoring_config.option_weights || {}), [option]: weight }
        }
      })
    }
  }

  return (
    <div className="space-y-4">
      {questions.map((q, index) => (
        <div key={index} className="bg-background border border-border-light rounded-lg">
          {/* Question header - always visible */}
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-sm font-medium text-foreground-muted w-6">{index + 1}.</span>
              <span className="text-sm font-medium text-foreground truncate">
                {q.text || 'Untitled question'}
              </span>
              <span className="text-xs text-foreground-muted bg-border-light px-2 py-0.5 rounded">{TYPE_LABELS[q.type]}</span>
              {q.required && <span className="text-xs text-error">Required</span>}
              {q.scoring_config && <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">Scored</span>}
            </div>
            <div className="flex items-center gap-2 ml-2">
              <button type="button" onClick={(e) => { e.stopPropagation(); removeQuestion(index) }}
                className="text-error hover:text-error text-sm">Remove</button>
              <svg className={`h-5 w-5 text-foreground-muted transition-transform ${expandedIndex === index ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Expanded editor */}
          {expandedIndex === index && (
            <div className="px-4 pb-4 space-y-4 border-t border-border-light pt-4">
              {/* Question text */}
              <input
                type="text"
                value={q.text}
                onChange={(e) => updateQuestion(index, { text: e.target.value })}
                placeholder="Enter your question"
                className="block w-full border-border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />

              <div className="grid grid-cols-3 gap-4">
                {/* Type */}
                <div>
                  <label className="block text-xs font-medium text-foreground-muted mb-1">Type</label>
                  <select
                    value={q.type}
                    onChange={(e) => handleTypeChange(index, e.target.value as QuestionType)}
                    className="block w-full border-border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    {Object.entries(TYPE_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Required */}
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input type="checkbox" checked={q.required}
                      onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                      className="mr-2 h-4 w-4 text-primary border-border rounded" />
                    <span className="text-sm text-foreground">Required</span>
                  </label>
                </div>

                {/* Scoring toggle */}
                <div className="flex items-end">
                  <label className="flex items-center">
                    <input type="checkbox" checked={!!q.scoring_config}
                      onChange={() => toggleScoring(index)}
                      className="mr-2 h-4 w-4 text-primary border-border rounded" />
                    <span className="text-sm text-foreground">Enable Scoring</span>
                  </label>
                </div>
              </div>

              {/* Choice options (Single or Multiple) */}
              {(q.type === 'SINGLE_CHOICE' || q.type === 'MULTIPLE_CHOICE') && (
                <div>
                  <label className="block text-xs font-medium text-foreground-muted mb-2">Options</label>
                  <div className="space-y-2">
                    {q.options.map((opt, optIdx) => (
                      <div key={optIdx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(index, optIdx, e.target.value)}
                          className="flex-1 border-border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                        {q.scoring_config?.type === 'weighted_options' && (
                          <input
                            type="number"
                            value={q.scoring_config.option_weights?.[opt] ?? 0}
                            onChange={(e) => updateOptionWeight(index, opt, parseFloat(e.target.value) || 0)}
                            className="w-20 border-border rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                            placeholder="pts"
                          />
                        )}
                        {q.options.length > 2 && (
                          <button type="button" onClick={() => removeOption(index, optIdx)}
                            className="text-error hover:text-error text-sm">X</button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-start gap-4">
                    <button type="button" onClick={() => addOption(index)}
                      className="text-sm text-primary hover:text-primary">+ Add Option</button>
                    <CsvOptionImport
                      existingOptionsCount={q.options.length}
                      onImport={(csvOptions, weights, mode) => {
                        const finalOptions = mode === 'append'
                          ? [...q.options, ...csvOptions.filter(o => !q.options.includes(o))]
                          : csvOptions
                        const updates: Partial<QuestionData> = { options: finalOptions }
                        if (weights) {
                          const mergedWeights = mode === 'append'
                            ? { ...(q.scoring_config?.option_weights || {}), ...weights }
                            : weights
                          updates.scoring_config = { type: 'weighted_options', option_weights: mergedWeights }
                        } else if (q.scoring_config?.type === 'weighted_options') {
                          const existingWeights = mode === 'append'
                            ? { ...(q.scoring_config.option_weights || {}) }
                            : {}
                          finalOptions.forEach(o => {
                            if (!(o in existingWeights)) existingWeights[o] = 0
                          })
                          updates.scoring_config = { ...q.scoring_config, option_weights: existingWeights }
                        }
                        updateQuestion(index, updates)
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Scoring config details */}
              {q.scoring_config && q.type === 'BOOLEAN' && (
                <div className="bg-primary/10 rounded p-3">
                  <label className="block text-xs font-medium text-primary-dark mb-2">Scoring: Points for Yes / No</label>
                  <div className="flex gap-4">
                    <div>
                      <label className="text-xs text-foreground-muted">Yes</label>
                      <input type="number" value={q.scoring_config.true_value ?? 1}
                        onChange={(e) => updateQuestion(index, { scoring_config: { ...q.scoring_config!, true_value: parseFloat(e.target.value) || 0 } })}
                        className="block w-20 border-border rounded-md sm:text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-foreground-muted">No</label>
                      <input type="number" value={q.scoring_config.false_value ?? 0}
                        onChange={(e) => updateQuestion(index, { scoring_config: { ...q.scoring_config!, false_value: parseFloat(e.target.value) || 0 } })}
                        className="block w-20 border-border rounded-md sm:text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {q.scoring_config && q.type === 'RATING' && (
                <div className="bg-primary/10 rounded p-3">
                  <p className="text-xs text-primary-dark">Rating scale: 0 to 5 points. Score equals the selected value.</p>
                </div>
              )}

              {q.scoring_config && q.type === 'TEXT' && (
                <div className="bg-primary/10 rounded p-3">
                  <p className="text-xs text-primary-dark">Text responses are not auto-scored (qualitative). Score: 0</p>
                </div>
              )}

              {q.scoring_config && (q.type === 'SINGLE_CHOICE' || q.type === 'MULTIPLE_CHOICE') && (
                <div className="bg-primary/10 rounded p-3">
                  <p className="text-xs text-primary-dark">Set point values for each option using the number fields above.</p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addQuestion}
        className="w-full py-3 border-2 border-dashed border-border rounded-lg text-sm font-medium text-foreground-muted hover:border-primary hover:text-primary transition-colors"
      >
        + Add Question
      </button>
    </div>
  )
}

export default QuestionBuilder
